import { CloseOutlined } from '@ant-design/icons';
import { useMyLeadsCRUDActions } from '@mm/state/actions/MyLeadsCRUDActions';
import { SearchAttributes } from '@mm/state/interfaces/leads';
import {
  MyLeadsFriendlyName,
  MyLeadsSearchQuery,
  MyLeadsFilterTypes,
} from '@mm/state/interfaces/myLeads';
import _ from 'lodash';
import React, { useState } from 'react';
import { RemoteSearchInput } from '../../../../../../../common/components/RemoteSearchInput/RemoteSearchInput';
import { convertToCamesCase } from '../../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import {
  convertObjectToMultidimensionalArray,
  getParamsByKeys,
} from '../helpers';
import { useFilterParams } from '../use-filter-params';
import styles from './MyLeadsLocationInputFilter.module.css';

export const MY_LEADS_LOCATION_FIELDS = [
  'LD_MUNICIPALITY',
  'LD_ADDRESS',
  'LD_CITY',
  'LD_STATE',
  'LD_ZIP',
  'LD_AI_COUNTY_NAME',
];

export const MyLeadsLocationInputFilter = ({
  disabled,
  filterParams,
  setFilterParams,
  exclude,
}: {
  disabled?: boolean;
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  exclude?: boolean;
}) => {
  const { retrieveLocationAttributes } = useMyLeadsCRUDActions();
  const { isFilterParamExist, getUpdatedFilterParams } = useFilterParams({
    filterParams,
  });

  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [locationResults, setLocationResults] = useState<SearchAttributes>({});

  const queryKey = exclude
    ? MyLeadsFilterTypes.excludedVariables
    : MyLeadsFilterTypes.variables;

  const locations = getParamsByKeys(
    filterParams.query?.[queryKey] || {},
    MY_LEADS_LOCATION_FIELDS,
  );
  const selectedLocations = convertObjectToMultidimensionalArray(locations);

  const remappedNames = (value: string) => {
    switch (convertToCamesCase(value)) {
      case 'ldAiCountyName':
        return MyLeadsFriendlyName.LD_AI_COUNTY_NAME;
      case 'ldState':
        return MyLeadsFriendlyName.LD_STATE;
      case 'ldCity':
        return MyLeadsFriendlyName.LD_CITY;
      case 'ldZip':
        return MyLeadsFriendlyName.LD_ZIP;
      case 'ldMunicipality':
        return MyLeadsFriendlyName.LD_MUNICIPALITY;
      default:
        return value;
    }
  };

  const mappedLocationResults = Object.entries(locationResults).flatMap(item =>
    item[1].map(i => ({
      key: item[0],
      name: i,
    })),
  );

  const fetchLocationAttributes = async query => {
    const trimmedQuery = query.trim();
    if (trimmedQuery) {
      setLocationSearchValue(trimmedQuery);
      const res: SearchAttributes = await retrieveLocationAttributes(
        trimmedQuery.toLowerCase(),
      );
      if (res) {
        setLocationResults(res);
      }
    }
  };

  const renderSuggestion = (suggestion: { key: string; name: string }) => (
    <div className={styles.suggestionContainer} key={suggestion.key}>
      <div className={styles.suggestion}>{suggestion.name}</div>
      {suggestion.key && (
        <div className={styles.key}>{remappedNames(suggestion.key)}</div>
      )}
    </div>
  );

  const onSelectedItem = (item: { key: string; name: string }) => {
    const itemExists = isFilterParamExist({
      key: item.key,
      value: item.name,
      type: exclude
        ? MyLeadsFilterTypes.excludedVariables
        : MyLeadsFilterTypes.variables,
    });
    let updatedQuery;

    if (!itemExists) {
      updatedQuery = getUpdatedFilterParams({
        key: item.key,
        value: item.name,
        isExisting: itemExists,
        type: exclude
          ? MyLeadsFilterTypes.excludedVariables
          : MyLeadsFilterTypes.variables,
      });
      setFilterParams(updatedQuery);
    }
  };

  const onDeleteItem = (item: [string, string]) => {
    const updatedQuery = getUpdatedFilterParams({
      key: item[0],
      value: item[1],
      isExisting: true,
      type: exclude
        ? MyLeadsFilterTypes.excludedVariables
        : MyLeadsFilterTypes.variables,
    });

    setFilterParams(updatedQuery);
  };

  return (
    <div className={styles.container}>
      <RemoteSearchInput
        value={locationSearchValue}
        placeholder="County, City, State, Zip Code"
        lengthLimit={1}
        disabled={disabled}
        onFetch={fetchLocationAttributes}
        results={mappedLocationResults}
        onSelectedItem={onSelectedItem}
        renderSuggestion={renderSuggestion}
        className={styles.input}
        style={{ border: '1px solid var(--color-gray-5)', borderRadius: '4px' }}
        withSearchIcon
        clearInputOnSelect
      />
      <div className={styles.locations}>
        {selectedLocations?.map(locationArr => (
          <div
            className={styles.locationItemWrapper}
            key={`${locationArr[0]}_${locationArr[1]}`}
          >
            <span className={styles.key}>{remappedNames(locationArr[0])}:</span>
            <span> {locationArr[1]}</span>
            <CloseOutlined
              className={styles.close}
              onClick={() => onDeleteItem(locationArr)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
